import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import BrowseBets from './pages/BrowseBets';
import CreateBet from './pages/CreateBet';
import WalletPage from './pages/WalletPage';
import RPSPage from './pages/RPSPage';
import AdminDashboard from './pages/AdminDashboard';
import BetDetails from './pages/BetDetails';
import BetOnAnythingPage from './pages/BetOnAnythingPage';
import BettingRulesPage from './pages/BettingRulesPage';
import RPSInfoPage from './pages/RPSInfoPage';
import SettingsPage from './pages/SettingsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ModeratorsPage from './pages/ModeratorsPage';
import RPSDuelPage from './pages/RPSDuelPage';
import KYCPage from './pages/KYCPage';
import TicketsPage from './pages/TicketsPage';
import ReferralsPage from './pages/ReferralsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ResponsibleGamblingPage from './pages/ResponsibleGamblingPage';
import HelpCentrePage from './pages/HelpCentrePage';
import ContactUsPage from './pages/ContactUsPage';
import FAQPage from './pages/FAQPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AIChatbot from './components/AIChatbot';

import './App.css';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin' && user.role !== 'moderator') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user && user !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Pages */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/bet-on-anything" element={<BetOnAnythingPage />} />
      <Route path="/betting-rules" element={<BettingRulesPage />} />
      <Route path="/rps-info" element={<RPSInfoPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/responsible-gambling" element={<ResponsibleGamblingPage />} />
      <Route path="/help" element={<HelpCentrePage />} />
      <Route path="/contact" element={<ContactUsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      
      {/* Auth Pages */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } 
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      
      {/* Protected Pages */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/browse" 
        element={
          <ProtectedRoute>
            <BrowseBets />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/create-bet" 
        element={
          <ProtectedRoute>
            <CreateBet />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/wallet" 
        element={
          <ProtectedRoute>
            <WalletPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/rps" 
        element={
          <ProtectedRoute>
            <RPSPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/bet/:betId" 
        element={
          <ProtectedRoute>
            <BetDetails />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/moderators" 
        element={
          <ProtectedRoute>
            <ModeratorsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/rps/duel/:challengeId" 
        element={
          <ProtectedRoute>
            <RPSDuelPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/kyc" 
        element={<ProtectedRoute><KYCPage /></ProtectedRoute>} 
      />
      <Route 
        path="/tickets" 
        element={<ProtectedRoute><TicketsPage /></ProtectedRoute>} 
      />
      <Route 
        path="/referrals" 
        element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <AppRoutes />
          <AIChatbot />
          <Toaster position="top-right" richColors />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
